// External Imports
import config from '@linc-technologies/linced-common/utils/config';
import {
  OAuthGrantType,
  setOAuthConfig,
  getOAuth,
} from '@linc-technologies/oauth2';
import type { IConfig, OAuth } from '@linc-technologies/oauth2';

// OAuthState is the payload carried through the OAuth `state` parameter.
export interface OAuthState {
  returnUrl?: string;
  [key: string]: unknown;
}

/**
 * getOAuthConfig returns an OAuth client configuration based on ember config
 * and available user-agent functionality.
 * @returns {IConfig}
 */
export function getOAuthConfig(): IConfig {
  const days = 30;

  return {
    debugEnabled: config.oauth.debugEnabled || false,
    authorizeUri: `${config.oauth.baseUri}/authorize`,
    redirectUri: `${window.location.origin}/callback`,
    tokenUri: `${config.oauth.baseUri}/token`,
    clientId: config.oauth.clientId,
    // `offline_access` enables the app to refresh its session as long as
    // the current user's session hasn't been revoked or marked invalid by
    // the upstream oauth server.
    scopes: [...config.oauth.scopes, 'offline_access'],
    grantType: OAuthGrantType.AuthorizationCode,
    autoRefreshLeeway: config.oauth.refreshLeeway,
    autoRefreshTimeout: 12,
    pkceEnabled: true,
    refreshSingleUse: true,
    refreshLifespanHint: 60 * 60 * 24 * days,
    dbName: 'heroV2',
  };
}

// setupOAuth pushes the OAuth client settings into the OAuth client module.
export function setupOAuth(): Promise<OAuth> {
  return setOAuthConfig(getOAuthConfig()).then((oauth) => {
    return oauth;
  });
}

// encodeState encodes data into the required payload for making an OAuth
// request with a state parameter. Uses URL-safe base64 (no +, /, or =) so
// the output can be safely placed in URLs without corruption by URLSearchParams
// or other parsers that treat + as a space.
export function encodeState(state: unknown = undefined): string {
  return btoa(JSON.stringify(state))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// decodeState attempts to parse the given string as a JSON blob.
// Handles both URL-safe base64 (new, -, _) and standard base64 (legacy, +, /)
// so that any states encoded before the URL-safe change are still decoded.
// It returns an empty object when the value is not a plain object.
export function decodeState(stateRes: string): OAuthState {
  try {
    const decoded = decodeURIComponent(stateRes);
    const normalized = decoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const state: unknown = JSON.parse(atob(padded));
    // JSON can parse to null, a primitive or an array. signIn sets
    // returnUrl on the state, thus it must be an object.
    if (state && typeof state === 'object' && !Array.isArray(state)) {
      return state as OAuthState;
    }
  } catch {
    // Maybe it's just a string, in which case we don't really care for it...
  }

  return {};
}

// oauthCallbackParams lists URL params that only make sense on the /callback
// URL itself and must never be carried into a rebuilt returnUrl: re-visiting
// a stale code/state (e.g. after a login retry from /callback) fails the
// exchange and is only harmless because startup.js recovers from it, so
// stripping them avoids that wasted round trip.
const oauthCallbackParams = [
  'code',
  'state',
  'error',
  'error_description',
  'error_hint',
  'error_uri',
];

// buildReturnUrl returns pathname with search stripped of oauth-callback-only
// params, for use as the state's returnUrl.
function buildReturnUrl(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  oauthCallbackParams.forEach((param) => params.delete(param));
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

// signIn pushes the client towards the OAuth provider.
export function signIn(): Promise<void> {
  const { pathname, search } = window.location;

  const params = new URLSearchParams(search);
  let newState = '';

  if (params.has('state')) {
    // Grab our state from the url params and decode it
    // has('state') above guarantees a value.
    const state = decodeState(params.get('state') as string);
    state.returnUrl = buildReturnUrl(pathname, search);
    // Encode the state again.
    newState = encodeState(state);
  } else {
    newState = encodeState({ returnUrl: buildReturnUrl(pathname, search) });
  }

  return getOAuth()
    .authorizeUri(newState)
    .then((signInUri) => window.location.replace(signInUri));
}
